import { Box, Button, Typography } from '@mui/material';
import React from 'react';

/**
 * FormErrorBoundary component to catch JavaScript errors in ActiveCampaign form embeds
 * Provides a lightweight, inline error UI instead of taking over the whole page
 *
 * @class FormErrorBoundary
 * @augments {React.Component}
 */
class FormErrorBoundary extends React.Component {
  /**
   * Creates an instance of FormErrorBoundary
   * @param {Object} props - Component props
   * @param {React.ReactNode} props.children - Child components to render
   * @param {string} [props.fallbackMessage] - Custom error message to display
   */
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /**
   * Update state when an error is caught
   * @param {Error} error - The error that was thrown
   * @returns {Object} New state object
   */
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  /**
   * Log error details to console
   * @param {Error} error - The error that was thrown
   * @param {Object} errorInfo - Additional error information
   */
  componentDidCatch(error, errorInfo) {
    console.error('FormErrorBoundary caught an error:', error, errorInfo);
  }

  /**
   * Reset error state to try rendering again
   */
  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  /**
   *
   */
  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            my: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: '#856404', fontWeight: 600, mb: 2 }}
          >
            {this.props.fallbackMessage || 'Unable to load form'}
          </Typography>
          <Typography sx={{ color: '#856404', mb: 3, fontSize: '0.95rem' }}>
            There was an error loading the contact form. Please try refreshing
            the page or contact us directly at{' '}
            <a
              href="mailto:info@rsm-collective.com"
              style={{
                color: 'var(--color-primary)',
                textDecoration: 'underline',
              }}
            >
              info@rsm-collective.com
            </a>
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              onClick={this.handleReset}
              sx={{
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-primary-text)',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'var(--color-primary-dark)',
                },
              }}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{
                borderColor: 'var(--color-primary)',
                color: 'var(--color-primary)',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'var(--color-primary-dark)',
                  backgroundColor:
                    'color-mix(in srgb, var(--color-primary) 4%, transparent)',
                },
              }}
            >
              Refresh Page
            </Button>
          </Box>

          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'var(--color-background-light)',
                borderRadius: '4px',
                textAlign: 'left',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: '#d32f2f',
                  display: 'block',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.error.toString()}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default FormErrorBoundary;
